<template>
    <div class="card" v-if="loaded">

        <div class="card-header border-0">
            <div class="row">
                <div class="col-4">
                    <h3 class="mb-0 text-uppercase">
                        {{ $t('common.logs') }}
                    </h3>
                </div>
                <div class="col-8 text-right">

                    <base-button type="secondary" size="sm" @click="clearFilters" v-if="hasFilters">
                        {{ $t('common.clear-filters') }}
                    </base-button>

                    <base-button type="primary" size="sm" @click="openFilters()">
                        {{ $t('common.more-filters') }}
                    </base-button>
                </div>
            </div>
        </div>

        <div class="pt-2">
            
            <table-control :paging="ctrl.paging" :search="true" v-model="search" ref="search"></table-control>

            <el-table class="table-responsive table-flush"
                    header-row-class-name="thead-light"
                    :data="ctrl.rows"
                    @sort-change="sortChange"
                    @filter-change="tableFiltersChange"
                    ref="table">

                <el-table-column :label="$t('common.cohort') + ' / ' + $t('common.degree')">
                    <template v-slot="{row}">
                        <h4 class="mb-0">
                            <a href="#" @click.prevent="filterByCohort(row.cohort)">
                                {{ row.cohort.name }}
                            </a>
                            /
                            <a href="#" @click.prevent="filterByDegree(row.degree)">
                                {{ row.degree.name }}
                            </a>
                        </h4>
                    </template>
                </el-table-column>

                <el-table-column :label="$t('common.simulations')">
                    <template v-slot="{row}">

                        <div class="p-1">
                            <base-button v-for="simulation in simulations(row)" :key="simulation.id"
                                type="secondary" size="sm" @click="features.openLog(row, simulation)" class="mr-3 pr-0"
                            >
                                <span :class="'status-bullet text-'+simulationColor(row, simulation)+' mr-2'" v-if="offlineSimulation(row.id, simulation.id)">
                                    <i class="fas fa-circle"></i>
                                </span>
                                <span class="mr-1" v-else>
                                </span>

                                {{ simulation.name }}

                                <actions-dropdown :button="true" :actions="actions(row, simulation)"></actions-dropdown>

                            </base-button>
                        </div>

                    </template>
                </el-table-column>

            </el-table>

            <table-pagination :paging="ctrl.paging"></table-pagination>
        </div>

        <portal to="modals">

            <form-modal v-model="ctrl.modals.filters"
                :title="$t('common.filters')"
            >
                <registration-filters
                    :modal="ctrl.modals.filters"
                    :value="filters"
                    @input="globalFiltersChange"
                >
                </registration-filters>

            </form-modal>

            <!-- Offline modals -->

            <confirm-modal v-model="ctrl.modals.offlineConfirmRemove">
                <p>{{ $t('pwa.remove-confirm') }}</p>
            </confirm-modal>

            <sync-modal v-model="ctrl.modals.offlineSync" :sync="sync" :autoRemoveAfterSync="true" @done="syncDone">
            </sync-modal>

            <base-modal
                v-model="ctrl.modals.offlineError"
                size="sm"
                modal-classes="modal-danger"
                modal-content-classes="bg-gradient-danger"
            >
                <div class="py-3 text-center">
                    <i class="tim-icons icon-t-warning icon-3x"></i>
                    <h4 class="heading mt-4">{{ offlineErrorMessage }}</h4>
                </div>
            </base-modal>

            <base-modal
                v-model="ctrl.modals.offlineConflict"
                modal-classes="modal-danger"
                modal-content-classes="bg-gradient-danger"
            >
                <div class="py-3 text-center">
                    <i class="tim-icons icon-t-warning icon-3x"></i>
                    <h4 class="heading mt-4">{{ $t('common.confirm-question') }}</h4>
                    <p>{{ $t('pwa.http-error-409') }}</p>
                </div>

                <template slot="footer">
                    <div class="text-center">
                        <base-button type="white" @click="confirmDownload">{{ $t('common.confirm') }}</base-button>
                        <base-button type="link" class="text-white" @click="cancelDownload">{{ $t('common.cancel') }}</base-button>
                    </div>
                </template>
            </base-modal>

        </portal>
    </div>
</template>

<script>
    import CrudController from '@trax/classes/CrudController'
    import CrudFiltering from '../../../classes/CrudFiltering'
    import RegistrationFilters from './RegistrationFilters'
    import LogFeatures from './LogFeatures'
    import SyncModal from '../offline/SyncModal'

    export default {

        components: {
            RegistrationFilters, SyncModal
        },

        data() {
            let features = new LogFeatures(this.$router, this.$auth)
            let filtering = new CrudFiltering('dashboard_logs')
            return {
                loaded: false,
                features,
                filtering,
                ctrl: new CrudController({
                    endpoint: '/socrates/api/front/dashboard/data/logs/registrations',
                    mapData: features.mapData,
                    unmapData: features.unmapData,
                    modals: ['filters', 'offlineError', 'offlineConflict', 'offlineConfirmRemove', 'offlineSync'],
                    paging: {
                        perPage: 10,
                        perPageOptions: [10, 25, 50],
                        total: 0
                    },
                    query: {
                        sort: ['-id'],
                        relations: ['cohort', 'degree'],
                        accessors: ['simulations'],
                    },
                    callbacks: {
                        listed: this.listed,
                    }
                }),
                search: '',
                filters: {},

                // Offline.
                offlineCurrentParams: null,
                offlineSimulations: [],
                offlineErrorMessage: null,
                sync: {
                    endpoint: '/socrates/api/front/offline/logs',
                    params: {},
                }
            }
        },

        created() {

            this.filtering.init(this)

            this.ctrl.query.filters = this.getFilters()

            this.ctrl.init(this, 'ctrl').then(resp => {
                this.loaded = true
            })

            this.loadOfflineStatus()
        },

        mounted() {
            if (this.filters.simpleSearch) {
                this.$refs.search.setFilter(this.filters.simpleSearch)
            }
        },

        watch: {
            search(val) {
                if (val === '') {
                    delete this.filters.simpleSearch
                } else {
                    this.filters.simpleSearch = val
                }
                this.filters = {...this.filters}    // Clone to force reactivity
                this.reload()
            }
        },

        computed: {

            empty() {
                return this.ctrl.rows.length == 0 && !this.search
            },

            hasFilters() {
                // First, remove null filters.
                Object.keys(this.filters).forEach(key => {
                    if (!this.filters[key]) {
                        delete this.filters[key]
                    }
                })

                return Object.keys(this.filters).length > 0 || this.ctrl.query.sort[0] != '-id'
            },
        },

        methods: {

            // -------------------- Offline methods.

            offlineSimulation(registrationId, simulationId) {
                return this.offlineSimulations.find(candidate => candidate.registration.id == registrationId && candidate.simulation.id == simulationId)
            },

            simulationColor(registration, simulation) {
                return this.offlineSimulation(registration.id, simulation.id).sync.color
            },
            
            simulationResyncable(registration, simulation) {
                const sync = this.offlineSimulation(registration.id, simulation.id).sync
                return sync.color == 'success' && (
                    sync.evaluations.synced
                    || sync.feedbacks.synced
                    || sync.issues.synced
                    || sync.observations.synced
                )
            },

            simulationDownloadable(simulation) {
                return true
            },

            actions(registration, simulation) {
                let actions = []
                if (this.offlineSimulation(registration.id, simulation.id)) {
                    if (this.simulationColor(registration, simulation) == 'danger') {
                        actions.push({
                            label: this.$t('pwa.sync'),
                            handler: this.syncSimulation,
                            data: { registration_id: registration.id, simulation_id: simulation.id }
                        })
                    }
                    actions.push({
                        label: this.$t('pwa.remove'),
                        handler: this.removeOfflineSimulation,
                        data: { registration_id: registration.id, simulation_id: simulation.id }
                    })
                    /*
                    if (this.simulationResyncable(registration, simulation)) {
                        actions.push({
                            label: this.$t('pwa.resync'),
                            handler: this.resyncSimulation,
                            data: { registration_id: registration.id, simulation_id: simulation.id }
                        })
                    }
                    */
                } else {
                    if (this.simulationDownloadable(simulation)) {
                        actions.push({
                            label: this.$t('pwa.download'),
                            handler: this.downloadSimulation,
                            data: { registration_id: registration.id, simulation_id: simulation.id }
                        })
                    }
                }
                return actions
            },

            downloadSimulation(params, force = true) {
                this.offlineCurrentParams = params

                this.$loader.show()
                axios.post(this.sync.endpoint, {
                    registration_id: params.registration_id,
                    simulation_id: params.simulation_id,
                    force: force
                })
                .then(resp => {
                    this.$loader.hide()
                    this.refreshOfflineSimulations(resp.data.data)
                }).catch(error => {
                    this.$loader.hide()
                    this.handleOfflineError(error)
                })
            },

            confirmDownload() {
                this.ctrl.modals.offlineConflict.show = false
                this.downloadSimulation(this.offlineCurrentParams, true)
            },

            cancelDownload() {
                this.ctrl.modals.offlineConflict.show = false
            },

            syncSimulation(params, force = false) {
                this.sync.params.registration_id = params.registration_id
                this.sync.params.simulation_id = params.simulation_id
                this.sync.params.force = force
                this.ctrl.modals.open('offlineSync')
            },

            // Currently unsued because local data is removed automatically after each sync.
            /*
            resyncSimulation(params) {
                this.syncSimulation(params, true)
            },
            */

            syncDone() {
                this.removeOfflineSimulationNow(this.sync.params)
                /*
                this.ctrl.list().then(resp => {
                    this.loadOfflineStatus()
                })
                */
            },

            removeOfflineSimulation(params) {
                this.ctrl.modals.open('offlineConfirmRemove').then(confirmResponse => {
                    if (confirmResponse.confirmed) {
                        this.removeOfflineSimulationNow(params)
                    }
                })
            },

            removeOfflineSimulationNow(params) {
                axios.delete(this.sync.endpoint, { data: {
                    registration_id: params.registration_id,
                    simulation_id: params.simulation_id,
                }})
                .then(resp => {
                    this.refreshOfflineSimulations(resp.data.data)
                }).catch(this.handleOfflineError)
            },

            loadOfflineStatus() {
                axios.get(this.sync.endpoint)
                .then(resp => {
                    this.refreshOfflineSimulations(resp.data.data)
                })
            },

            refreshOfflineSimulations(simulations) {
                this.offlineSimulations = simulations
                this.$emit('refresh-offline-status')
            },

            handleOfflineError(error) {
                if (!error.response) {
                    return
                }
                switch(error.response.status) {
                    case 409:
                        this.ctrl.modals.open('offlineConflict')
                        break
                    case 403:
                    case 404:
                    case 405:
                    case 444:
                        this.offlineErrorMessage = this.$t('pwa.http-error-' + error.response.status)
                        this.ctrl.modals.open('offlineError')
                        break
                    default:
                        this.$auth.handleErrorVm(error, this)
                }
            },


            // --------------------


            simulations(registration) {
                return registration.simulations.filter(simulation => !simulation.disabled)
            },

            sortChange(data) {
                this.filtering.sortChange(data, ['-id'])
            },

            tableFiltersChange(data) {
                this.filtering.filterChange(data)
            },

            globalFiltersChange(data) {
                this.filters = { ...this.filters, ...data}
                this.reload()
            },

            filterByDegree(degree) {
                this.filters.degree_id = degree.id
                this.filters = {...this.filters}    // Clone to force reactivity
                this.reload()
            },

            filterByCohort(cohort) {
                this.filters.cohort_id = cohort.id
                this.filters = {...this.filters}    // Clone to force reactivity
                this.reload()
            },

            clearFilters() {
                this.filtering.clearFilters({}, ['-id'])
            },

            listed() {
                this.filtering.saveUiConfig()
            },

            getFilters() {
                let filters = {$and: []}
                
                // Search.
                if (this.filters.simpleSearch) {
                    filters.$and.push({ simpleSearch: this.filters.simpleSearch})
                }
                
                // Addtional filters.
                if (this.filters.cohort_id) {
                    filters.$and.push(
                        {cohort_id: this.filters.cohort_id}
                    )
                }
                if (this.filters.degree_id) {
                    filters.$and.push(
                        {degree_id: this.filters.degree_id}
                    )
                }

                if (filters.$and.length == 0) {
                    filters = {}
                }
                return filters
            },

            reload() {
                this.ctrl.filter(
                    this.getFilters()
                )
            },

            openFilters() {
                this.ctrl.modals.open('filters').then(filters => {
                    this.reload()
                })
            },
        }
    }
</script>

<style scoped>

    .card >>> th,
    .card >>> td {
        padding-right: 0;
    }

    .status-bullet {
        font-size: 8px;
        vertical-align: middle;
    }

</style>
